import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Alert, Anchor, Badge, Button, Container, Grid, Group, Skeleton, Stack, Tabs, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconArrowLeft, IconArrowRight, IconFiles, IconListCheck } from '@tabler/icons-react';
import DealHeader from '@/components/app/deals/DealHeader';
import DealChatButton from '@/components/app/deals/DealChatButton';
import DealStepper from '@/components/app/deals/DealStepper';
import DealStepPanel from '@/components/app/deals/DealStepPanel';
import DealStepReadonly from '@/components/app/deals/DealStepReadonly';
import DealDocuments from '@/components/app/deals/DealDocuments';
import { useBargainSteps, useDocumentsTree, useRefreshDeal } from '@/hooks/useDeals';
import { isStepNavigable } from '@/lib/dealSteps';
import { getStatus, isServiceUnavailable } from '@/api/errors';
import { countTreeDocuments } from '@/lib/dealDocuments';
import { ROUTES } from '@/routes/paths';
import tabClasses from '@/components/common/scrollTabs.module.css';
import BulletinSection from '@/components/common/BulletinSection';
import layoutClasses from './DealViewPage.module.css';

// Режим карточки сделки: пошаговый просмотр или единый архив документов.
type DealView = 'steps' | 'docs';

// Карточка одной сделки (Ф1, read-only): шапка + вкладки «Шаги | Документы» (единый таб-паттерн
// платформы, как в «Моя компания»). Во вкладке «Шаги» — степпер (sticky) + сводка выбранного шага с
// документами этого шага; единый архив всех документов сделки живёт во вкладке «Документы» (один
// экземпляр, без повтора под каждым шагом), счётчик архива — бейджем на самой вкладке.
export default function DealViewPage() {
  const { t } = useTranslation();
  const { dealId } = useParams();
  const id = Number(dealId);
  const valid = dealId != null && dealId !== '' && !Number.isNaN(id);

  const steps = useBargainSteps(valid ? id : null);
  const docs = useDocumentsTree(valid ? id : null);
  const refresh = useRefreshDeal(valid ? id : null);
  const refreshing = steps.isFetching || docs.isFetching;

  // id шага, выбранного для просмотра. undefined → следуем за текущим шагом сделки.
  const [selectedStepId, setSelectedStepId] = useState<number | undefined>(undefined);
  // Режим: пошаговый просмотр (по умолчанию) или единый архив документов.
  const [view, setView] = useState<DealView>('steps');

  // Назад к списку — общий блок для всех экранов ошибок/недоступности.
  const backLink = (
    <Button component={Link} to={ROUTES.APP_DEALS} variant="default" leftSection={<IconArrowLeft size={16} />}>
      {t('app.deals.back')}
    </Button>
  );

  if (!valid) {
    return (
      <Container size="lg" py="xl">
        <Stack gap="md" align="flex-start">
          <Title order={3}>{t('app.deals.notFound')}</Title>
          {backLink}
        </Stack>
      </Container>
    );
  }

  if (steps.isError) {
    const status = getStatus(steps.error);
    const unavailable = isServiceUnavailable(steps.error);
    // 400/403/404 — сделки нет или пользователь не участник (бэк отдаёт 400 не-участнику): спокойный
    // экран «недоступна», без «Повторить» — повтор детерминированно не поможет.
    if (status === 400 || status === 403 || status === 404) {
      return (
        <Container size="lg" py="xl">
          <Stack gap="md" align="flex-start">
            <Title order={3}>{t('app.deals.notFound')}</Title>
            <Text c="gray.7">{t('app.deals.notFoundHint')}</Text>
            {backLink}
          </Stack>
        </Container>
      );
    }
    return (
      <Container size="lg" py="xl">
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
          <Group justify="space-between" wrap="nowrap">
            <Text>{unavailable ? t('auth.errors.unavailable') : t('auth.errors.generic')}</Text>
            <Button variant="subtle" size="compact-sm" onClick={() => steps.refetch()}>
              {t('common.retry')}
            </Button>
          </Group>
        </Alert>
      </Container>
    );
  }

  if (steps.isLoading || !steps.data) {
    return (
      <Container size="lg" py="xl">
        <Stack gap="lg">
          <Skeleton h={90} />
          <Grid>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Skeleton h={320} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Skeleton h={320} />
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>
    );
  }

  const info = steps.data;
  const stepsList = info.stepsList ?? [];

  // Текущий шаг ведём по isCurrent. Выбранный валидируем (навигируем и существует) — иначе текущий.
  const currentStep = stepsList.find((s) => s.isCurrent);
  const selectedStep =
    selectedStepId != null ? stepsList.find((s) => s.id === selectedStepId && isStepNavigable(s)) : undefined;
  const viewingCurrent = !selectedStep || selectedStep.id === currentStep?.id;

  const handleSelectStep = (stepId: number) => {
    setSelectedStepId(stepId === currentStep?.id ? undefined : stepId);
  };

  const docNodes = docs.data?.nodes ?? [];
  const docCount = countTreeDocuments(docNodes);

  // Карточка единого архива документов (режим «Документы») — один экземпляр на всю сделку.
  const documentsCard = (
    <BulletinSection title={t('app.deals.docs.title')} spaced>
      {docs.isLoading ? (
        <Stack gap="sm">
          <Skeleton h={48} />
          <Skeleton h={48} />
        </Stack>
      ) : docs.isError ? (
        <Group justify="space-between" wrap="nowrap">
          <Text fz="sm" c="gray.7">
            {t('app.deals.docs.error')}
          </Text>
          <Button variant="subtle" size="compact-sm" onClick={() => docs.refetch()}>
            {t('common.retry')}
          </Button>
        </Group>
      ) : (
        <DealDocuments nodes={docNodes} />
      )}
    </BulletinSection>
  );

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Чат по сделке (через агента) — компактная кнопка в правом верхнем углу шапки, перед «Обновить». */}
        <DealHeader
          info={info}
          onRefresh={refresh}
          refreshing={refreshing}
          chatAction={<DealChatButton bargainId={id} userRole={info.userRole} status={info.bargainStatus} />}
        />

        {/* Вкладки уровня всей сделки: пошаговый просмотр vs единый архив документов (со счётчиком). */}
        <Tabs value={view} onChange={(value) => value && setView(value as DealView)} keepMounted={false}>
          <Tabs.List className={tabClasses.list}>
            <Tabs.Tab value="steps" leftSection={<IconListCheck size={16} />}>
              {t('app.deals.view.steps')}
            </Tabs.Tab>
            <Tabs.Tab
              value="docs"
              leftSection={<IconFiles size={16} />}
              rightSection={
                docCount > 0 ? (
                  <Badge size="sm" variant="light" color="gray">
                    {docCount}
                  </Badge>
                ) : undefined
              }
            >
              {t('app.deals.view.documents')}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="steps" pt="lg">
            <div className={layoutClasses.layout}>
              <Stack gap="lg">
                {viewingCurrent || !selectedStep ? (
                  <DealStepPanel dealId={id} info={info} />
                ) : (
                  <DealStepReadonly
                    info={info}
                    step={selectedStep}
                    onBackToCurrent={() => setSelectedStepId(undefined)}
                  />
                )}

                {/* Документы шага показаны внутри шага; полный архив — отдельной вкладкой (без повтора). */}
                {docCount > 0 ? (
                  <Group justify="flex-end">
                    <Anchor
                      component="button"
                      type="button"
                      fz="sm"
                      c="brand.7"
                      underline="never"
                      onClick={() => setView('docs')}
                    >
                      <Group gap={4} wrap="nowrap">
                        {t('app.deals.view.allDocumentsCount', { n: docCount })}
                        <IconArrowRight size={14} />
                      </Group>
                    </Anchor>
                  </Group>
                ) : null}
              </Stack>

              <aside className={layoutClasses.aside}>
                <BulletinSection title={t('app.deals.stepper.title')}>
                  {stepsList.length > 0 ? (
                    <DealStepper
                      steps={stepsList}
                      selectedId={selectedStep?.id ?? currentStep?.id}
                      onSelect={handleSelectStep}
                    />
                  ) : (
                    <Text fz="sm" c="gray.7" pt="sm">
                      {t('app.deals.stepper.empty')}
                    </Text>
                  )}
                </BulletinSection>
              </aside>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="docs" pt="lg">
            {documentsCard}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
